import { notification } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useCreateInterpretationSnapshotMutation } from '../../../api/variantApi';
import { NOTIFICATION_MESSAGES } from '../../constants';
import type {
  InterpretationSnapshotCreateRequest,
  InterpretationSnapshotPayload,
  VariantHistory,
} from '../../redux';
import type { InterpretationSnapshotCaptureHandle } from '../snapshotCapture';
import { registerInterpretationSnapshotCapture } from '../snapshotCapture';

type SnapshotLogicOptions = {
  interpretationRef: React.RefObject<HTMLDivElement>;
  variant: InterpretationSnapshotPayload['variant'] | null;
  variantId?: string;
  stages?: InterpretationSnapshotPayload['stages'];
  interpretationZone?: InterpretationSnapshotPayload['interpretation_zone'];
  argumentsForClassification?: InterpretationSnapshotPayload['arguments_for_classification'];
  molecularImpactHeader?: InterpretationSnapshotPayload['molecular_impact_header'];
  comments?: InterpretationSnapshotPayload['comments'];
  history?: InterpretationSnapshotPayload['history'];
  references?: InterpretationSnapshotPayload['references'];
  linkouts?: InterpretationSnapshotPayload['linkouts'];
  geneFactsOverview?: InterpretationSnapshotPayload['gene_facts_overview'];
  isLoading: boolean;
  isLinkoutsLoaded: boolean;
  isHeaderLoaded: boolean;
  areCommentsLoaded: boolean;
  isHistoryLoaded: boolean;
  isInterpretationZoneLoaded: boolean;
  areReferencesLoaded: boolean;
  areGeneFactsLoaded: boolean;
  areStagesLoaded: boolean;
  areArgumentsLoaded: boolean;
  refreshHistory?: () => Promise<VariantHistory | undefined>;
  stageStatus?: string;
  registerSnapshotCapture?: boolean;
};

const useInterpretationSnapshotLogic = ({
  interpretationRef,
  variant,
  variantId,
  stages,
  interpretationZone,
  argumentsForClassification,
  molecularImpactHeader,
  comments,
  history,
  references,
  linkouts,
  geneFactsOverview,
  isLoading,
  isLinkoutsLoaded,
  isHeaderLoaded,
  areCommentsLoaded,
  isHistoryLoaded,
  isInterpretationZoneLoaded,
  areReferencesLoaded,
  areGeneFactsLoaded,
  areStagesLoaded,
  areArgumentsLoaded,
  refreshHistory,
  stageStatus,
  registerSnapshotCapture = false,
}: SnapshotLogicOptions) => {
  const [isSnapshotCaptureExpanded, setIsSnapshotCaptureExpanded] =
    useState(false);
  const [createInterpretationSnapshot] =
    useCreateInterpretationSnapshotMutation();

  const snapshotPayload = useMemo<InterpretationSnapshotPayload | null>(() => {
    if (!variant) {
      return null;
    }

    return {
      variant,
      stages: stages ?? [],
      interpretation_zone: interpretationZone ?? null,
      arguments_for_classification: argumentsForClassification ?? [],
      molecular_impact_header: molecularImpactHeader ?? null,
      comments: comments ?? [],
      history: history ?? null,
      references: references ?? [],
      linkouts: linkouts ?? null,
      gene_facts_overview: geneFactsOverview ?? null,
    };
  }, [
    argumentsForClassification,
    comments,
    geneFactsOverview,
    history,
    interpretationZone,
    linkouts,
    molecularImpactHeader,
    references,
    stages,
    variant,
  ]);

  const isSnapshotDataReady = Boolean(
    snapshotPayload &&
    !isLoading &&
    isLinkoutsLoaded &&
    isHeaderLoaded &&
    areCommentsLoaded &&
    isHistoryLoaded &&
    isInterpretationZoneLoaded &&
    areReferencesLoaded &&
    areGeneFactsLoaded &&
    areStagesLoaded &&
    areArgumentsLoaded,
  );

  const waitForExpandedSnapshot = () =>
    new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => resolve());
      });
    });

  const getSnapshotHtml = useCallback(() => {
    const interpretationElement = interpretationRef.current;
    if (!interpretationElement) {
      return undefined;
    }

    const snapshotElement = interpretationElement.cloneNode(
      true,
    ) as HTMLDivElement;
    snapshotElement
      .querySelectorAll('[data-snapshot-exclude]')
      .forEach((element) => element.remove());
    snapshotElement
      .querySelectorAll(
        'button, [role="button"], .clinical-obs__load-more-proband-btn-wrapper',
      )
      .forEach((element) => element.remove());

    snapshotElement.querySelectorAll('table').forEach((table) => {
      table.classList.add('interpretation-snapshot-table');
      table.removeAttribute('width');
      table.style.removeProperty('table-layout');
      table.style.removeProperty('width');

      table.querySelectorAll('colgroup').forEach((colgroup) => {
        colgroup.remove();
      });
      table.querySelectorAll('col, th, td').forEach((cell) => {
        cell.removeAttribute('width');
        (cell as HTMLElement).style.removeProperty('width');
      });
    });
    snapshotElement.classList.add('interpretation--snapshot');

    return snapshotElement.outerHTML;
  }, [interpretationRef]);

  const prepareSnapshot =
    useCallback(async (): Promise<InterpretationSnapshotCreateRequest | null> => {
      if (!snapshotPayload || !variantId || !isSnapshotDataReady) {
        return null;
      }

      const refreshedHistory = await refreshHistory?.();
      setIsSnapshotCaptureExpanded(true);

      try {
        await waitForExpandedSnapshot();

        return {
          schema_version: 1,
          payload: {
            ...snapshotPayload,
            history: refreshedHistory ?? snapshotPayload.history,
            rendered_html: getSnapshotHtml(),
          },
        };
      } finally {
        setIsSnapshotCaptureExpanded(false);
      }
    }, [
      getSnapshotHtml,
      isSnapshotDataReady,
      refreshHistory,
      snapshotPayload,
      variantId,
    ]);

  const persistSnapshot = useCallback(
    async (
      snapshotRequest: InterpretationSnapshotCreateRequest,
      showSuccessNotification = true,
    ): Promise<boolean> => {
      if (!variantId) {
        return false;
      }

      try {
        await createInterpretationSnapshot({
          variant_id: variantId,
          requestData: snapshotRequest,
        }).unwrap();
        if (showSuccessNotification) {
          notification.success({
            message: NOTIFICATION_MESSAGES.SNAPSHOT_SAVED,
            description: NOTIFICATION_MESSAGES.SNAPSHOT_SAVED_DESCRIPTION,
          });
        }
        return true;
      } catch {
        notification.error({
          message: NOTIFICATION_MESSAGES.SNAPSHOT_SAVE_FAILED,
          description: NOTIFICATION_MESSAGES.SNAPSHOT_SAVE_FAILED_DESCRIPTION,
        });
        return false;
      }
    },
    [createInterpretationSnapshot, variantId],
  );

  const saveSnapshot = useCallback(
    async (showSuccessNotification = true): Promise<boolean> => {
      const snapshotRequest = await prepareSnapshot();
      if (!snapshotRequest) {
        return false;
      }

      return persistSnapshot(snapshotRequest, showSuccessNotification);
    },
    [persistSnapshot, prepareSnapshot],
  );

  const handleAutomaticRevisionSnapshot = useCallback(
    async (
      preparedSnapshot: InterpretationSnapshotCreateRequest | null,
    ): Promise<boolean> => {
      if (preparedSnapshot) {
        return persistSnapshot(preparedSnapshot, false);
      }

      return saveSnapshot(false);
    },
    [persistSnapshot, saveSnapshot],
  );

  const snapshotCaptureState =
    useRef<InterpretationSnapshotCaptureHandle | null>(null);
  snapshotCaptureState.current = {
    isReady: () => isSnapshotDataReady,
    shouldCapture: () => stageStatus === 'accepted',
    prepare: prepareSnapshot,
    persist: (snapshotRequest) => persistSnapshot(snapshotRequest, false),
  };

  useEffect(() => {
    if (!registerSnapshotCapture) {
      return undefined;
    }

    const captureHandle: InterpretationSnapshotCaptureHandle = {
      isReady: () => snapshotCaptureState.current?.isReady() ?? false,
      shouldCapture: () =>
        snapshotCaptureState.current?.shouldCapture() ?? false,
      prepare: async () => {
        for (let attempt = 0; attempt < 200; attempt += 1) {
          if (snapshotCaptureState.current?.isReady()) {
            return snapshotCaptureState.current.prepare();
          }

          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, 50);
          });
        }

        return null;
      },
      persist: (snapshotRequest) =>
        snapshotCaptureState.current?.persist(snapshotRequest) ??
        Promise.resolve(false),
    };

    return registerInterpretationSnapshotCapture(captureHandle);
  }, [registerSnapshotCapture]);

  return {
    isSnapshotCaptureExpanded,
    prepareSnapshot,
    handleAutomaticRevisionSnapshot,
  };
};

export default useInterpretationSnapshotLogic;
